import React from 'react';
import without from 'lodash/without';
import { useTranslation } from 'common/i18n/hooks';
import { MultiMetricsQueryBlock } from 'common/query-builder/query-block/multi-metrics-menu/query-block';
import type { MultiMetricsMenuItem, MultiMetricsSectionItem } from 'common/query-builder/query-block/multi-metrics-menu/types';
import { MenuType } from 'common/query-builder/query-block/multi-metrics-menu/types';
import { useMetricEntries } from 'common/report/insights/contexts/metrics';
import * as BookmarkTypes from 'common/types/reports/bookmark';
import { getNewMetricEntry } from 'iron/common/query-builder/query-entry-section/row-types/multi-metrics-entry/metric-helper';
import type { RowProps } from 'iron/common/query-builder/query-entry-section/row-types/new-metric-entry/types/catalog';
import { ReportName } from 'common/report/constants';

/** defines which metric types are available for selection on a given report mode */
const RestrictedMetricTypesForReportName: Partial<Record<ReportName, BookmarkTypes.MetricType>> = {
  [ReportName.Funnels]: BookmarkTypes.MetricType.Funnel,
  [ReportName.Retention]: BookmarkTypes.MetricType.Retention
};

/**
 * Component for selecting a metric: saved formula, saved metric, cohorts, events, behaviors, etc...
 * This component directly modifies the bookmark by using the `useMetricEntries` hook
 */
export function MetricSelector(props: RowProps) {
  var _props$entry$id;
  const {
    t
  } = useTranslation();
  const {
    selectMetricItem
  } = useMetricEntries();
  const label = props.entry.name || t("query-builder.metrics.new-definition");
  const handleMetricSelect = (selectedItem: MultiMetricsSectionItem) => {
    var _props$sectionProps$e;
    selectMetricItem({
      index: props.index,
      parentMetricIndex: props.parentMetricIndex,
      selectedItem,
      dataGroupId: ((_props$sectionProps$e = props.sectionProps.entries) === null || _props$sectionProps$e === void 0 || (_props$sectionProps$e = _props$sectionProps$e.at(-1)) === null || _props$sectionProps$e === void 0 ? void 0 : _props$sectionProps$e.dataGroupId) || null
    });
  };
  const handleMetricCreate = (menuType: MenuType) => {
    var _props$sectionProps$e2;
    /**
     * @todo(metrics) a neat workflow addition here would be to try to preserve any event selection from existing metric state
     * for instance if you have a simple behavior with 3 events, if you create a funnel from this metric, retain those 3 events
     * as funnel steps
     */
    const entry = getNewMetricEntry({
      menuType,
      entries: (_props$sectionProps$e2 = props.sectionProps.entries) !== null && _props$sectionProps$e2 !== void 0 ? _props$sectionProps$e2 : []
    });
    props.updateEntry(entry);
  };
  const isFormulaMetric = props.entry.type === BookmarkTypes.TopLevelMetricType.NewFormulaEntry;

  /** filters out the available metric selections */
  let excludeTypes: Array<BookmarkTypes.MetricType> = [];
  if (props.parentMetricIndex !== undefined) {
    /** disables selecting metrics or formulas inside a formula definition **/
    excludeTypes = [BookmarkTypes.MetricType.Formula];
  } else if (props.sectionProps.reportName && props.sectionProps.reportName in RestrictedMetricTypesForReportName) {
    /** makes sure only funnel or retention metrics are available for selection in those report modes */
    excludeTypes = without(Object.values(BookmarkTypes.MetricType), RestrictedMetricTypesForReportName[props.sectionProps.reportName]!, BookmarkTypes.MetricType.SavedMetric);
  }
  return <MultiMetricsQueryBlock projectHasCohorts selected={{
    menuType: isFormulaMetric ? MenuType.Formula : MenuType.SavedMetric,
    label: "",
    value: {
      id: (_props$entry$id = props.entry.id) !== null && _props$entry$id !== void 0 ? _props$entry$id : undefined,
      name: label
    } as MultiMetricsMenuItem
  }} contextPanelParentElement={props.contextPanelParentElement} excludedMetricsRowTypes={excludeTypes} allDataGroups={props.sectionProps.dataGroupsEnabled} dataGroupId={props.sectionProps.dataGroupId} hideEditControls={props.sectionProps.hideEditControls} queryOrigin={props.sectionProps.queryOrigin} showSessionEvent={props.sectionProps.showSessions} supportB2bCohorts={props.sectionProps.supportB2bCohorts} trackName={props.sectionProps.trackName} projectId={String(props.sectionProps.projectId)} workspaceId={String(props.sectionProps.workspaceId)} userId={Number(props.sectionProps.userId)} onSelect={handleMetricSelect} onCreate={(ev: {
    detail: {
      item: MenuType;
    };
  }) => handleMetricCreate(ev.detail.item)}
  /** @todo(metrics) figure out tracking */ onTrack={() => null} onClose={() => null} onOpen={() => null} data-sentry-element="MultiMetricsQueryBlock" data-sentry-component="MetricSelector" data-sentry-source-file="metric-selector.tsx" />;
}